import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// אופן תשלום של הוצאה כללית (רשימה קבועה - תואם enum ExpensePaymentMethod בשרת)
export enum ExpensePaymentMethod {
  Cash = 1,
  BankTransfer = 2,
  CreditCard = 3,
  Check = 4,
  StandingOrder = 5,
  Other = 99,
}

export const ExpensePaymentMethodOptions: { value: ExpensePaymentMethod; label: string }[] = [
  { value: ExpensePaymentMethod.Cash, label: 'מזומן' },
  { value: ExpensePaymentMethod.BankTransfer, label: 'העברה בנקאית' },
  { value: ExpensePaymentMethod.CreditCard, label: 'כרטיס אשראי' },
  { value: ExpensePaymentMethod.Check, label: "צ'ק" },
  { value: ExpensePaymentMethod.StandingOrder, label: 'הוראת קבע' },
  { value: ExpensePaymentMethod.Other, label: 'אחר' },
];

// Types
export interface GeneralExpenseDto {
  id: number;
  categoryId: number;
  categoryName: string;
  amount: number;
  expenseDate: string;
  notes?: string;
  paymentMethod: number;
  paymentMethodName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGeneralExpenseDto {
  categoryId: number;
  amount: number;
  expenseDate: string;
  notes?: string;
  paymentMethod: number;
}

export type UpdateGeneralExpenseDto = CreateGeneralExpenseDto;

export interface GeneralExpenseFilter {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  categoryId?: number;
  paymentMethod?: number;
  dateFrom?: string;
  dateTo?: string;
  year?: number;
  month?: number;
}

export interface GeneralExpensePagedResult {
  items: GeneralExpenseDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ExpenseCategoryDto {
  id: number;
  name: string;
}

export interface ExpenseCategoryBreakdownDto {
  categoryId: number;
  categoryName: string;
  total: number;
}

export interface GeneralExpenseSummaryDto {
  total: number;
  count: number;
  byCategory: ExpenseCategoryBreakdownDto[];
}

const BASE = `${API_BASE_URL}/generalexpenses`;

export const generalExpensesApi = {
  list: async (filter: GeneralExpenseFilter): Promise<GeneralExpensePagedResult> => {
    const response = await axios.get<GeneralExpensePagedResult>(BASE, { params: filter });
    return response.data;
  },

  getById: async (id: number): Promise<GeneralExpenseDto> => {
    const response = await axios.get<GeneralExpenseDto>(`${BASE}/${id}`);
    return response.data;
  },

  create: async (dto: CreateGeneralExpenseDto): Promise<GeneralExpenseDto> => {
    const response = await axios.post<GeneralExpenseDto>(BASE, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateGeneralExpenseDto): Promise<GeneralExpenseDto> => {
    const response = await axios.put<GeneralExpenseDto>(`${BASE}/${id}`, dto);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await axios.delete(`${BASE}/${id}`);
  },

  getSummary: async (filter: GeneralExpenseFilter): Promise<GeneralExpenseSummaryDto> => {
    const response = await axios.get<GeneralExpenseSummaryDto>(`${BASE}/summary`, {
      params: filter,
    });
    return response.data;
  },

  getCategories: async (): Promise<ExpenseCategoryDto[]> => {
    const response = await axios.get<ExpenseCategoryDto[]>(`${BASE}/categories`);
    return response.data;
  },

  createCategory: async (name: string): Promise<ExpenseCategoryDto> => {
    const response = await axios.post<ExpenseCategoryDto>(`${BASE}/categories`, { name });
    return response.data;
  },
};

// Helpers (פורמט he-IL, תואם שאר השירותים)
export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
